import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { onboardingAPI } from '../../services/api';

const nutritionSchema = z.object({
  eatTimesPerDay: z.enum(['1', '2', '3', '4', '5', '6+'], { required_error: 'Please select meals per day' }),
  currentDiet: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  pastMeals: z.string().min(1, 'Past meals are required'),
  foodAllergies: z.string().optional(),
  digestiveIssues: z.string().optional(),
  coffeeAmount: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  softDrinksAmount: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  alcoholAmount: z.enum(['Yes', 'No'], { required_error: 'Please select an option' })
});

type NutritionFormData = z.infer<typeof nutritionSchema>;

interface NutritionFormProps {
  onComplete: () => void;
}

const NutritionForm: React.FC<NutritionFormProps> = ({ onComplete }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<NutritionFormData>({
    resolver: zodResolver(nutritionSchema)
  });

  const onSubmit = async (data: NutritionFormData) => {
    setIsSubmitting(true);
    try {
      await onboardingAPI.submitNutrition(data);
      onComplete();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to submit nutrition form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const RadioGroup = ({ name, question, error }: { name: keyof NutritionFormData; question: string; error?: string }) => (
    <div className="mb-6">
      <fieldset>
        <legend className="text-sm font-medium text-gray-900 mb-3">
          **{question}
        </legend>
        <div className="flex space-x-6">
          <label className="flex items-center">
            <input
              {...register(name)}
              type="radio"
              value="Yes"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">Yes</span>
          </label>
          <label className="flex items-center">
            <input
              {...register(name)}
              type="radio"
              value="No"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">No</span>
          </label>
        </div>
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      </fieldset>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white shadow rounded-lg p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Nutrition Assessment</h1>
          <p className="text-red-600 font-medium">**FIELDS ARE REQUIRED</p>
          <p className="text-gray-600 mt-2">
            Understanding your current eating habits helps us create a nutrition plan that works for your lifestyle and goals.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Eating Frequency */}
          <div>
            <label htmlFor="eatTimesPerDay" className="block text-sm font-medium text-gray-700 mb-2">
              **How many times per day do you typically eat?
            </label>
            <select
              {...register('eatTimesPerDay')}
              id="eatTimesPerDay"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.eatTimesPerDay ? 'border-red-300' : 'border-gray-300'
              }`}
              required
            >
              <option value="">Select meals per day</option>
              <option value="1">1 time</option>
              <option value="2">2 times</option>
              <option value="3">3 times</option>
              <option value="4">4 times</option>
              <option value="5">5 times</option>
              <option value="6+">6+ times</option>
            </select>
            {errors.eatTimesPerDay && (
              <p className="mt-1 text-sm text-red-600">{errors.eatTimesPerDay.message}</p>
            )}
          </div>

          {/* Current Diet */}
          <RadioGroup
            name="currentDiet"
            question="Are you currently following a specific diet?"
            error={errors.currentDiet?.message}
          />

          {/* Past Meals */}
          <div>
            <label htmlFor="pastMeals" className="block text-sm font-medium text-gray-700 mb-2">
              **Please describe what you ate for the past 3 days
            </label>
            <textarea
              {...register('pastMeals')}
              id="pastMeals"
              rows={6}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.pastMeals ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Include breakfast, lunch, dinner, and any snacks. Be as detailed as possible about portion sizes and preparation methods..."
              required
            />
            {errors.pastMeals && (
              <p className="mt-1 text-sm text-red-600">{errors.pastMeals.message}</p>
            )}
          </div>

          {/* Food Allergies */}
          <div>
            <label htmlFor="foodAllergies" className="block text-sm font-medium text-gray-700 mb-2">
              Food Allergies or Intolerances (Optional)
            </label>
            <textarea
              {...register('foodAllergies')}
              id="foodAllergies"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="List any food allergies, intolerances, or sensitivities you have..."
            />
          </div>

          {/* Digestive Issues */}
          <div>
            <label htmlFor="digestiveIssues" className="block text-sm font-medium text-gray-700 mb-2">
              Digestive Issues (Optional)
            </label>
            <textarea
              {...register('digestiveIssues')}
              id="digestiveIssues"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe any digestive issues, sensitivities, or gastrointestinal problems you experience..."
            />
          </div>

          {/* Beverage Consumption */}
          <div className="space-y-4">
            <RadioGroup
              name="coffeeAmount"
              question="Do you consume coffee regularly?"
              error={errors.coffeeAmount?.message}
            />

            <RadioGroup
              name="softDrinksAmount"
              question="Do you consume soft drinks or sugary beverages regularly?"
              error={errors.softDrinksAmount?.message}
            />

            <RadioGroup
              name="alcoholAmount"
              question="Do you consume alcohol regularly?"
              error={errors.alcoholAmount?.message}
            />
          </div>

          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
            <h3 className="text-sm font-medium text-green-800 mb-2">Nutrition Tips:</h3>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• Be honest about your current eating habits - this helps us create realistic goals</li>
              <li>• Include portion sizes and preparation methods when describing meals</li>
              <li>• Don't forget to mention snacks and beverages</li>
              <li>• Your nutrition plan will be personalized based on your goals and preferences</li>
            </ul>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Submitting...' : 'Continue >'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NutritionForm;
